import { useState, useRef, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { motion } from "framer-motion"
import { ChevronDown } from "lucide-react"
import { GOAL_TO_PLAN, NUTRITION_PLANS, type NutritionPlanId } from "@/data/nutritionPlans"
import { BottomNav } from "@/components/BottomNav"
import { IconTile, InfoBanner, NutritionBackdrop, NutritionHeader, PlanPill, SectionLabel } from "@/components/nutrition/NutritionUI"
import { CARD, MACRO_COLORS, haloSurface } from "@/components/nutrition/theme"
import { cn, formatIntFr } from "@/lib/utils"

// ─── TYPES ──────────────────────────────────────────────────────────────────────

interface Food {
  n: string
  q: string
  p: number
  g: number
  l: number
  k: number
}

interface MealTotal {
  p: number
  g: number
  l: number
  k: number
}

interface Meal {
  name: string
  foods: Food[]
  total: MealTotal
}

interface DayData {
  total: { prot: number; glu: number; lip: number; kcal: number }
  meals: Meal[]
}

interface Plan {
  label: string
  targetKcal: number
  macros: { prot: number; glu: number; lip: number }
  days: DayData[]
}

// ─── DATA ───────────────────────────────────────────────────────────────────────

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"]
const DAY_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]

const MEAL_ICONS: Record<string, string> = {
  "Petit-déjeuner": "☀️",
  "Déjeuner": "🍽️",
  "Collation": "🥜",
  "Dîner": "🌙",
}

const PLANS: Record<string, Plan> = {
  seche: {
    label: "Sèche",
    targetKcal: 1937,
    macros: { prot: 113, glu: 259, lip: 50 },
    days: [
      {
        total: { prot: 106, glu: 123, lip: 34, kcal: 1194 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "40g", p: 5.2, g: 24, l: 2.8, k: 142 }, { n: "Blanc d'œuf ×3", q: "99g", p: 10.8, g: 0.6, l: 0, k: 48 }, { n: "Banane", q: "80g", p: 0.9, g: 18.4, l: 0.2, k: 71 }, { n: "Café noir", q: "200ml", p: 0.2, g: 0, l: 0, k: 4 }], total: { p: 17.1, g: 43, l: 3, k: 265 } },
          { name: "Déjeuner", foods: [{ n: "Blanc de poulet", q: "150g", p: 34.5, g: 0, l: 2.3, k: 159 }, { n: "Riz basmati", q: "120g", p: 3.6, g: 30, l: 0.5, k: 138 }, { n: "Brocoli", q: "150g", p: 4.2, g: 10.5, l: 0.6, k: 51 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 42.3, g: 40.5, l: 8, k: 389 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "150g", p: 15, g: 6, l: 0, k: 84 }, { n: "Amandes", q: "15g", p: 3.2, g: 3.3, l: 7.5, k: 87 }], total: { p: 18.2, g: 9.3, l: 7.5, k: 171 } },
          { name: "Dîner", foods: [{ n: "Saumon", q: "120g", p: 24, g: 0, l: 15.6, k: 236 }, { n: "Patate douce", q: "100g", p: 1.6, g: 20, l: 0.1, k: 86 }, { n: "Haricots verts", q: "150g", p: 2.7, g: 10.5, l: 0.3, k: 47 }], total: { p: 28.3, g: 30.5, l: 16, k: 369 } },
        ],
      },
      {
        total: { prot: 118, glu: 112, lip: 18, kcal: 1066 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Fromage blanc 0%", q: "150g", p: 12, g: 6, l: 0.2, k: 74 }, { n: "Myrtilles", q: "80g", p: 0.6, g: 11.2, l: 0.2, k: 46 }, { n: "Flocons d'avoine", q: "30g", p: 3.9, g: 18, l: 2.1, k: 107 }], total: { p: 16.5, g: 35.2, l: 2.5, k: 227 } },
          { name: "Déjeuner", foods: [{ n: "Escalope de dinde", q: "150g", p: 36, g: 0, l: 3, k: 171 }, { n: "Quinoa", q: "130g", p: 5.7, g: 27.3, l: 2.5, k: 156 }, { n: "Courgette", q: "200g", p: 1.8, g: 2.8, l: 0.8, k: 30 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 43.5, g: 30.1, l: 10.9, k: 398 } },
          { name: "Collation", foods: [{ n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Pomme", q: "150g", p: 0.5, g: 21, l: 0.3, k: 78 }], total: { p: 24.5, g: 23, l: 1.8, k: 196 } },
          { name: "Dîner", foods: [{ n: "Cabillaud", q: "150g", p: 27, g: 0, l: 1.5, k: 122 }, { n: "Épinards", q: "150g", p: 4.4, g: 5.7, l: 0.6, k: 35 }, { n: "Riz complet", q: "80g", p: 2.1, g: 18.4, l: 0.7, k: 88 }], total: { p: 33.5, g: 24.1, l: 2.8, k: 245 } },
        ],
      },
      {
        total: { prot: 106, glu: 89, lip: 33, kcal: 1057 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Pain complet", q: "40g", p: 4, g: 17.6, l: 1, k: 96 }, { n: "Avocat", q: "40g", p: 0.8, g: 3.6, l: 6, k: 64 }], total: { p: 20, g: 22, l: 19, k: 332 } },
          { name: "Déjeuner", foods: [{ n: "Thon en boîte", q: "120g", p: 31.2, g: 0, l: 0.6, k: 130 }, { n: "Pâtes complètes", q: "120g", p: 6, g: 30, l: 1.2, k: 151 }, { n: "Tomate", q: "100g", p: 0.9, g: 3.9, l: 0.2, k: 18 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 38.1, g: 33.9, l: 6.6, k: 340 } },
          { name: "Collation", foods: [{ n: "Cottage cheese", q: "100g", p: 12, g: 3, l: 4, k: 96 }, { n: "Fraises", q: "100g", p: 0.7, g: 8, l: 0.3, k: 33 }], total: { p: 12.7, g: 11, l: 4.3, k: 129 } },
          { name: "Dîner", foods: [{ n: "Crevettes", q: "150g", p: 31.5, g: 0, l: 2.3, k: 149 }, { n: "Légumes grillés", q: "200g", p: 1.9, g: 7.4, l: 0.7, k: 42 }, { n: "Pomme de terre", q: "80g", p: 1.6, g: 14.4, l: 0.1, k: 65 }], total: { p: 35, g: 21.8, l: 3.1, k: 256 } },
        ],
      },
      {
        total: { prot: 120, glu: 113, lip: 21, kcal: 1097 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "40g", p: 5.2, g: 24, l: 2.8, k: 142 }, { n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Banane", q: "70g", p: 0.8, g: 16.1, l: 0.2, k: 62 }], total: { p: 30, g: 42.1, l: 4.5, k: 322 } },
          { name: "Déjeuner", foods: [{ n: "Bœuf 5% MG", q: "130g", p: 28.6, g: 0, l: 6.5, k: 173 }, { n: "Lentilles", q: "120g", p: 10.8, g: 20.4, l: 0.5, k: 128 }, { n: "Salade verte", q: "80g", p: 1, g: 1.8, l: 0.2, k: 11 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 40.4, g: 22.2, l: 11.8, k: 353 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "150g", p: 15, g: 6, l: 0, k: 84 }, { n: "Kiwi", q: "80g", p: 0.9, g: 12, l: 0.4, k: 49 }], total: { p: 15.9, g: 18, l: 0.4, k: 133 } },
          { name: "Dîner", foods: [{ n: "Filet de porc", q: "130g", p: 28.6, g: 0, l: 3.9, k: 150 }, { n: "Haricots verts", q: "150g", p: 2.7, g: 10.5, l: 0.3, k: 47 }, { n: "Riz basmati", q: "80g", p: 2.4, g: 20, l: 0.3, k: 92 }], total: { p: 33.7, g: 30.5, l: 4.5, k: 289 } },
        ],
      },
      {
        total: { prot: 120, glu: 93, lip: 24, kcal: 1038 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Fromage blanc 0%", q: "150g", p: 12, g: 6, l: 0.2, k: 74 }, { n: "Flocons d'avoine", q: "30g", p: 3.9, g: 18, l: 2.1, k: 107 }, { n: "Fraises", q: "80g", p: 0.6, g: 6.4, l: 0.2, k: 26 }], total: { p: 16.5, g: 30.4, l: 2.5, k: 207 } },
          { name: "Déjeuner", foods: [{ n: "Blanc de poulet", q: "150g", p: 34.5, g: 0, l: 2.3, k: 159 }, { n: "Patate douce", q: "130g", p: 2.1, g: 26, l: 0.1, k: 112 }, { n: "Brocoli", q: "120g", p: 3.4, g: 8.4, l: 0.5, k: 41 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 40, g: 34.4, l: 7.5, k: 353 } },
          { name: "Collation", foods: [{ n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Amandes", q: "15g", p: 3.2, g: 3.3, l: 7.5, k: 87 }], total: { p: 27.2, g: 5.3, l: 9, k: 205 } },
          { name: "Dîner", foods: [{ n: "Tilapia", q: "150g", p: 30, g: 0, l: 3, k: 147 }, { n: "Asperges", q: "150g", p: 3.3, g: 5.9, l: 0.2, k: 30 }, { n: "Quinoa", q: "80g", p: 3.5, g: 16.8, l: 1.5, k: 96 }], total: { p: 36.8, g: 22.7, l: 4.7, k: 273 } },
        ],
      },
      {
        total: { prot: 109, glu: 101, lip: 38, kcal: 1158 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Pain complet", q: "50g", p: 5, g: 22, l: 1.3, k: 120 }, { n: "Tomate", q: "80g", p: 0.7, g: 3.1, l: 0.2, k: 14 }], total: { p: 20.9, g: 25.9, l: 13.5, k: 306 } },
          { name: "Déjeuner", foods: [{ n: "Saumon", q: "130g", p: 26, g: 0, l: 16.9, k: 256 }, { n: "Riz complet", q: "100g", p: 2.6, g: 23, l: 0.9, k: 110 }, { n: "Épinards", q: "120g", p: 3.5, g: 4.6, l: 0.5, k: 28 }], total: { p: 32.1, g: 27.6, l: 18.3, k: 394 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "150g", p: 15, g: 6, l: 0, k: 84 }, { n: "Pomme", q: "120g", p: 0.4, g: 16.8, l: 0.2, k: 62 }], total: { p: 15.4, g: 22.8, l: 0.2, k: 146 } },
          { name: "Dîner", foods: [{ n: "Escalope de dinde", q: "130g", p: 31.2, g: 0, l: 2.6, k: 148 }, { n: "Courgette", q: "200g", p: 1.8, g: 2.8, l: 0.8, k: 30 }, { n: "Pois chiches", q: "80g", p: 7.2, g: 21.6, l: 2.1, k: 134 }], total: { p: 40.2, g: 24.4, l: 5.5, k: 312 } },
        ],
      },
      {
        total: { prot: 106, glu: 109, lip: 17, kcal: 1003 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "40g", p: 5.2, g: 24, l: 2.8, k: 142 }, { n: "Blanc d'œuf ×3", q: "99g", p: 10.8, g: 0.6, l: 0, k: 48 }, { n: "Myrtilles", q: "80g", p: 0.6, g: 11.2, l: 0.2, k: 46 }, { n: "Café noir", q: "200ml", p: 0.2, g: 0, l: 0, k: 4 }], total: { p: 16.8, g: 35.8, l: 3, k: 240 } },
          { name: "Déjeuner", foods: [{ n: "Thon frais", q: "150g", p: 37.5, g: 0, l: 1.5, k: 164 }, { n: "Pâtes", q: "100g", p: 5.5, g: 26, l: 1, k: 131 }, { n: "Poivron", q: "100g", p: 1, g: 6, l: 0.3, k: 27 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 44, g: 32, l: 7.4, k: 363 } },
          { name: "Collation", foods: [{ n: "Cottage cheese", q: "100g", p: 12, g: 3, l: 4, k: 96 }, { n: "Kiwi", q: "80g", p: 0.9, g: 12, l: 0.4, k: 49 }], total: { p: 12.9, g: 15, l: 4.4, k: 145 } },
          { name: "Dîner", foods: [{ n: "Blanc de poulet", q: "130g", p: 29.9, g: 0, l: 2, k: 138 }, { n: "Carotte", q: "100g", p: 0.8, g: 8, l: 0.2, k: 36 }, { n: "Pomme de terre", q: "100g", p: 2, g: 18, l: 0.1, k: 81 }], total: { p: 32.7, g: 26, l: 2.3, k: 255 } },
        ],
      },
    ],
  },
  maintien: {
    label: "Maintien",
    targetKcal: 2421,
    macros: { prot: 113, glu: 380, lip: 50 },
    days: [
      {
        total: { prot: 130, glu: 190, lip: 61, kcal: 1778 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "60g", p: 7.8, g: 36, l: 4.2, k: 213 }, { n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Banane", q: "100g", p: 1.1, g: 23, l: 0.3, k: 89 }, { n: "Lait demi-écrémé", q: "150ml", p: 4.8, g: 7.4, l: 2.3, k: 69 }], total: { p: 28.9, g: 67.2, l: 18.8, k: 543 } },
          { name: "Déjeuner", foods: [{ n: "Blanc de poulet", q: "170g", p: 39.1, g: 0, l: 2.6, k: 180 }, { n: "Riz basmati", q: "180g", p: 5.4, g: 45, l: 0.7, k: 207 }, { n: "Brocoli", q: "150g", p: 4.2, g: 10.5, l: 0.6, k: 51 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 48.7, g: 55.5, l: 13.1, k: 521 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "150g", p: 15, g: 6, l: 0, k: 84 }, { n: "Amandes", q: "20g", p: 4.2, g: 4.4, l: 10, k: 116 }, { n: "Pomme", q: "120g", p: 0.4, g: 16.8, l: 0.2, k: 62 }], total: { p: 19.6, g: 27.2, l: 10.2, k: 262 } },
          { name: "Dîner", foods: [{ n: "Saumon", q: "140g", p: 28, g: 0, l: 18.2, k: 276 }, { n: "Patate douce", q: "150g", p: 2.4, g: 30, l: 0.2, k: 129 }, { n: "Haricots verts", q: "150g", p: 2.7, g: 10.5, l: 0.3, k: 47 }], total: { p: 33.1, g: 40.5, l: 18.7, k: 452 } },
        ],
      },
      {
        total: { prot: 134, glu: 164, lip: 46, kcal: 1589 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Pain complet", q: "80g", p: 8, g: 35.2, l: 2, k: 192 }, { n: "Beurre de cacahuète", q: "15g", p: 3.8, g: 3, l: 7.5, k: 94 }, { n: "Œuf entier ×1", q: "60g", p: 7.6, g: 0.4, l: 6, k: 86 }, { n: "Jus d'orange", q: "200ml", p: 1.4, g: 20, l: 0.2, k: 86 }], total: { p: 20.8, g: 58.6, l: 15.7, k: 458 } },
          { name: "Déjeuner", foods: [{ n: "Bœuf haché 10%", q: "150g", p: 31.5, g: 0, l: 15, k: 261 }, { n: "Pâtes", q: "180g", p: 9.9, g: 46.8, l: 1.8, k: 236 }, { n: "Tomate", q: "100g", p: 0.9, g: 3.9, l: 0.2, k: 18 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 42.3, g: 50.7, l: 21.6, k: 556 } },
          { name: "Collation", foods: [{ n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Banane", q: "100g", p: 1.1, g: 23, l: 0.3, k: 89 }], total: { p: 25.1, g: 25, l: 1.8, k: 207 } },
          { name: "Dîner", foods: [{ n: "Escalope de dinde", q: "160g", p: 38.4, g: 0, l: 3.2, k: 182 }, { n: "Quinoa", q: "130g", p: 5.7, g: 27.3, l: 2.5, k: 156 }, { n: "Courgette", q: "200g", p: 1.8, g: 2.8, l: 0.8, k: 30 }], total: { p: 45.9, g: 30.1, l: 6.5, k: 368 } },
        ],
      },
      {
        total: { prot: 128, glu: 128, lip: 38, kcal: 1348 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "50g", p: 6.5, g: 30, l: 3.5, k: 178 }, { n: "Fromage blanc 0%", q: "150g", p: 12, g: 6, l: 0.2, k: 74 }, { n: "Fraises", q: "100g", p: 0.7, g: 8, l: 0.3, k: 33 }, { n: "Miel", q: "10g", p: 0, g: 8.2, l: 0, k: 33 }], total: { p: 19.2, g: 52.2, l: 4, k: 318 } },
          { name: "Déjeuner", foods: [{ n: "Filet de porc", q: "160g", p: 35.2, g: 0, l: 4.8, k: 184 }, { n: "Lentilles", q: "150g", p: 13.5, g: 25.5, l: 0.6, k: 161 }, { n: "Salade verte", q: "80g", p: 1, g: 1.8, l: 0.2, k: 11 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 49.7, g: 27.3, l: 14.8, k: 439 } },
          { name: "Collation", foods: [{ n: "Cottage cheese", q: "120g", p: 14.4, g: 3.6, l: 4.8, k: 115 }, { n: "Amandes", q: "20g", p: 4.2, g: 4.4, l: 10, k: 116 }], total: { p: 18.6, g: 8, l: 14.8, k: 231 } },
          { name: "Dîner", foods: [{ n: "Crevettes", q: "170g", p: 35.7, g: 0, l: 2.6, k: 168 }, { n: "Riz complet", q: "150g", p: 3.9, g: 34.5, l: 1.4, k: 165 }, { n: "Poivron", q: "100g", p: 1, g: 6, l: 0.3, k: 27 }], total: { p: 40.6, g: 40.5, l: 4.3, k: 360 } },
        ],
      },
      {
        total: { prot: 143, glu: 144, lip: 43, kcal: 1493 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Pain complet", q: "60g", p: 6, g: 26.4, l: 1.5, k: 144 }, { n: "Avocat", q: "50g", p: 1, g: 4.5, l: 7.5, k: 80 }, { n: "Café noir", q: "200ml", p: 0.2, g: 0, l: 0, k: 4 }], total: { p: 22.4, g: 31.7, l: 21, k: 400 } },
          { name: "Déjeuner", foods: [{ n: "Blanc de poulet", q: "170g", p: 39.1, g: 0, l: 2.6, k: 180 }, { n: "Patate douce", q: "180g", p: 2.9, g: 36, l: 0.2, k: 155 }, { n: "Brocoli", q: "150g", p: 4.2, g: 10.5, l: 0.6, k: 51 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 46.2, g: 46.5, l: 12.6, k: 469 } },
          { name: "Collation", foods: [{ n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Banane", q: "90g", p: 1, g: 20.7, l: 0.3, k: 80 }, { n: "Beurre de cacahuète", q: "10g", p: 2.5, g: 2, l: 5, k: 63 }], total: { p: 27.5, g: 24.7, l: 6.8, k: 261 } },
          { name: "Dîner", foods: [{ n: "Thon en boîte", q: "140g", p: 36.4, g: 0, l: 0.7, k: 151 }, { n: "Pâtes complètes", q: "150g", p: 7.5, g: 37.5, l: 1.5, k: 189 }, { n: "Épinards", q: "100g", p: 2.9, g: 3.8, l: 0.4, k: 23 }], total: { p: 46.8, g: 41.3, l: 2.6, k: 363 } },
        ],
      },
      {
        total: { prot: 114, glu: 143, lip: 39, kcal: 1352 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "50g", p: 6.5, g: 30, l: 3.5, k: 178 }, { n: "Blanc d'œuf ×3", q: "99g", p: 10.8, g: 0.6, l: 0, k: 48 }, { n: "Myrtilles", q: "100g", p: 0.7, g: 14, l: 0.3, k: 57 }, { n: "Lait demi-écrémé", q: "100ml", p: 3.2, g: 4.9, l: 1.5, k: 46 }], total: { p: 21.2, g: 49.5, l: 5.3, k: 329 } },
          { name: "Déjeuner", foods: [{ n: "Saumon", q: "150g", p: 30, g: 0, l: 19.5, k: 296 }, { n: "Riz basmati", q: "150g", p: 4.5, g: 37.5, l: 0.6, k: 173 }, { n: "Asperges", q: "120g", p: 2.6, g: 4.7, l: 0.1, k: 24 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 37.1, g: 42.2, l: 24.8, k: 534 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "150g", p: 15, g: 6, l: 0, k: 84 }, { n: "Kiwi", q: "100g", p: 1.1, g: 15, l: 0.5, k: 61 }], total: { p: 16.1, g: 21, l: 0.5, k: 145 } },
          { name: "Dîner", foods: [{ n: "Bœuf 5% MG", q: "150g", p: 33, g: 0, l: 7.5, k: 200 }, { n: "Pomme de terre", q: "150g", p: 3, g: 27, l: 0.2, k: 122 }, { n: "Champignons", q: "100g", p: 3.1, g: 3.3, l: 0.3, k: 22 }], total: { p: 39.1, g: 30.3, l: 8, k: 344 } },
        ],
      },
      {
        total: { prot: 136, glu: 119, lip: 49, kcal: 1433 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Pain complet", q: "80g", p: 8, g: 35.2, l: 2, k: 192 }, { n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Tomate", q: "80g", p: 0.7, g: 3.1, l: 0.2, k: 14 }, { n: "Fromage blanc 3%", q: "100g", p: 7, g: 4, l: 3, k: 69 }], total: { p: 30.9, g: 43.1, l: 17.2, k: 447 } },
          { name: "Déjeuner", foods: [{ n: "Escalope de dinde", q: "170g", p: 40.8, g: 0, l: 3.4, k: 194 }, { n: "Boulgour", q: "160g", p: 4.8, g: 28.8, l: 0.3, k: 133 }, { n: "Haricots verts", q: "150g", p: 2.7, g: 10.5, l: 0.3, k: 47 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 48.3, g: 39.3, l: 13.2, k: 457 } },
          { name: "Collation", foods: [{ n: "Cottage cheese", q: "100g", p: 12, g: 3, l: 4, k: 96 }, { n: "Amandes", q: "20g", p: 4.2, g: 4.4, l: 10, k: 116 }], total: { p: 16.2, g: 7.4, l: 14, k: 212 } },
          { name: "Dîner", foods: [{ n: "Cabillaud", q: "170g", p: 30.6, g: 0, l: 1.7, k: 138 }, { n: "Pois chiches", q: "100g", p: 9, g: 27, l: 2.6, k: 168 }, { n: "Salade verte", q: "80g", p: 1, g: 1.8, l: 0.2, k: 11 }], total: { p: 40.6, g: 28.8, l: 4.5, k: 317 } },
        ],
      },
      {
        total: { prot: 132, glu: 160, lip: 44, kcal: 1550 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "60g", p: 7.8, g: 36, l: 4.2, k: 213 }, { n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Banane", q: "100g", p: 1.1, g: 23, l: 0.3, k: 89 }], total: { p: 32.9, g: 61, l: 6, k: 420 } },
          { name: "Déjeuner", foods: [{ n: "Bœuf haché 10%", q: "160g", p: 33.6, g: 0, l: 16, k: 278 }, { n: "Quinoa", q: "160g", p: 7, g: 33.6, l: 3, k: 192 }, { n: "Carotte", q: "100g", p: 0.8, g: 8, l: 0.2, k: 36 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 41.4, g: 41.6, l: 28.4, k: 589 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "150g", p: 15, g: 6, l: 0, k: 84 }, { n: "Pomme", q: "120g", p: 0.4, g: 16.8, l: 0.2, k: 62 }, { n: "Beurre de cacahuète", q: "10g", p: 2.5, g: 2, l: 5, k: 63 }], total: { p: 17.9, g: 24.8, l: 5.2, k: 209 } },
          { name: "Dîner", foods: [{ n: "Blanc de poulet", q: "150g", p: 34.5, g: 0, l: 2.3, k: 159 }, { n: "Riz complet", q: "130g", p: 3.4, g: 29.9, l: 1.2, k: 143 }, { n: "Courgette", q: "200g", p: 1.8, g: 2.8, l: 0.8, k: 30 }], total: { p: 39.7, g: 32.7, l: 4.3, k: 332 } },
        ],
      },
    ],
  },
  masse: {
    label: "Prise de masse",
    targetKcal: 2663,
    macros: { prot: 113, glu: 440, lip: 50 },
    days: [
      {
        total: { prot: 169, glu: 226, lip: 79, kcal: 2254 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "80g", p: 10.4, g: 48, l: 5.6, k: 284 }, { n: "Œuf entier ×3", q: "180g", p: 22.8, g: 1.2, l: 18, k: 258 }, { n: "Banane", q: "120g", p: 1.3, g: 27.6, l: 0.4, k: 107 }, { n: "Lait demi-écrémé", q: "200ml", p: 6.4, g: 9.8, l: 3, k: 92 }], total: { p: 40.9, g: 86.6, l: 27, k: 741 } },
          { name: "Déjeuner", foods: [{ n: "Blanc de poulet", q: "200g", p: 46, g: 0, l: 3, k: 212 }, { n: "Riz basmati", q: "220g", p: 6.6, g: 55, l: 0.9, k: 253 }, { n: "Brocoli", q: "150g", p: 4.2, g: 10.5, l: 0.6, k: 51 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 56.8, g: 65.5, l: 13.7, k: 599 } },
          { name: "Collation", foods: [{ n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Beurre de cacahuète", q: "20g", p: 5, g: 4, l: 10, k: 125 }, { n: "Pain complet", q: "40g", p: 4, g: 17.6, l: 1, k: 96 }], total: { p: 33, g: 23.6, l: 12.5, k: 339 } },
          { name: "Dîner", foods: [{ n: "Saumon", q: "160g", p: 32, g: 0, l: 20.8, k: 315 }, { n: "Patate douce", q: "200g", p: 3.2, g: 40, l: 0.2, k: 172 }, { n: "Haricots verts", q: "150g", p: 2.7, g: 10.5, l: 0.3, k: 47 }, { n: "Huile d'olive", q: "5ml", p: 0, g: 0, l: 4.6, k: 41 }], total: { p: 37.9, g: 50.5, l: 25.9, k: 575 } },
        ],
      },
      {
        total: { prot: 161, glu: 208, lip: 75, kcal: 2120 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Pain complet", q: "100g", p: 10, g: 44, l: 2.5, k: 240 }, { n: "Beurre de cacahuète", q: "20g", p: 5, g: 4, l: 10, k: 125 }, { n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Jus d'orange", q: "250ml", p: 1.8, g: 25, l: 0.3, k: 108 }], total: { p: 32, g: 73.8, l: 24.8, k: 645 } },
          { name: "Déjeuner", foods: [{ n: "Bœuf haché 10%", q: "180g", p: 37.8, g: 0, l: 18, k: 313 }, { n: "Pâtes", q: "220g", p: 12.1, g: 57.2, l: 2.2, k: 288 }, { n: "Tomate", q: "100g", p: 0.9, g: 3.9, l: 0.2, k: 18 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 50.8, g: 61.1, l: 29.6, k: 702 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "200g", p: 20, g: 8, l: 0, k: 112 }, { n: "Amandes", q: "25g", p: 5.3, g: 5.5, l: 12.5, k: 145 }, { n: "Banane", q: "100g", p: 1.1, g: 23, l: 0.3, k: 89 }], total: { p: 26.4, g: 36.5, l: 12.8, k: 346 } },
          { name: "Dîner", foods: [{ n: "Escalope de dinde", q: "180g", p: 43.2, g: 0, l: 3.6, k: 205 }, { n: "Quinoa", q: "160g", p: 7, g: 33.6, l: 3, k: 192 }, { n: "Courgette", q: "200g", p: 1.8, g: 2.8, l: 0.8, k: 30 }], total: { p: 52, g: 36.4, l: 7.4, k: 427 } },
        ],
      },
      {
        total: { prot: 153, glu: 183, lip: 40, kcal: 1683 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "70g", p: 9.1, g: 42, l: 4.9, k: 249 }, { n: "Fromage blanc 3%", q: "150g", p: 10.5, g: 6, l: 4.5, k: 104 }, { n: "Miel", q: "15g", p: 0, g: 12.3, l: 0, k: 49 }, { n: "Myrtilles", q: "100g", p: 0.7, g: 14, l: 0.3, k: 57 }], total: { p: 20.3, g: 74.3, l: 9.7, k: 459 } },
          { name: "Déjeuner", foods: [{ n: "Filet de porc", q: "180g", p: 39.6, g: 0, l: 5.4, k: 207 }, { n: "Lentilles", q: "180g", p: 16.2, g: 30.6, l: 0.7, k: 193 }, { n: "Salade verte", q: "80g", p: 1, g: 1.8, l: 0.2, k: 11 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 56.8, g: 32.4, l: 15.5, k: 494 } },
          { name: "Collation", foods: [{ n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Banane", q: "100g", p: 1.1, g: 23, l: 0.3, k: 89 }, { n: "Beurre d'amande", q: "15g", p: 3.2, g: 2.9, l: 8.1, k: 95 }], total: { p: 28.3, g: 27.9, l: 9.9, k: 302 } },
          { name: "Dîner", foods: [{ n: "Crevettes", q: "200g", p: 42, g: 0, l: 3, k: 198 }, { n: "Riz complet", q: "180g", p: 4.7, g: 41.4, l: 1.6, k: 198 }, { n: "Poivron", q: "120g", p: 1.2, g: 7.2, l: 0.4, k: 32 }], total: { p: 47.9, g: 48.6, l: 5, k: 428 } },
        ],
      },
      {
        total: { prot: 151, glu: 166, lip: 82, kcal: 1954 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Œuf entier ×3", q: "180g", p: 22.8, g: 1.2, l: 18, k: 258 }, { n: "Pain complet", q: "80g", p: 8, g: 35.2, l: 2, k: 192 }, { n: "Avocat", q: "60g", p: 1.2, g: 5.4, l: 9, k: 96 }], total: { p: 32, g: 41.8, l: 29, k: 546 } },
          { name: "Déjeuner", foods: [{ n: "Blanc de poulet", q: "200g", p: 46, g: 0, l: 3, k: 212 }, { n: "Patate douce", q: "220g", p: 3.5, g: 44, l: 0.2, k: 189 }, { n: "Épinards", q: "120g", p: 3.5, g: 4.6, l: 0.5, k: 28 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 53, g: 48.6, l: 12.9, k: 512 } },
          { name: "Collation", foods: [{ n: "Cottage cheese", q: "150g", p: 18, g: 4.5, l: 6, k: 144 }, { n: "Amandes", q: "25g", p: 5.3, g: 5.5, l: 12.5, k: 145 }, { n: "Pomme", q: "120g", p: 0.4, g: 16.8, l: 0.2, k: 62 }], total: { p: 23.7, g: 26.8, l: 18.7, k: 351 } },
          { name: "Dîner", foods: [{ n: "Saumon", q: "150g", p: 30, g: 0, l: 19.5, k: 296 }, { n: "Pâtes complètes", q: "180g", p: 9, g: 45, l: 1.8, k: 227 }, { n: "Champignons", q: "100g", p: 3.1, g: 3.3, l: 0.3, k: 22 }], total: { p: 42.1, g: 48.3, l: 21.6, k: 545 } },
        ],
      },
      {
        total: { prot: 158, glu: 203, lip: 38, kcal: 1760 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "70g", p: 9.1, g: 42, l: 4.9, k: 249 }, { n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Banane", q: "100g", p: 1.1, g: 23, l: 0.3, k: 89 }, { n: "Lait d'avoine", q: "150ml", p: 1.5, g: 10.5, l: 2.3, k: 69 }], total: { p: 35.7, g: 77.5, l: 9, k: 525 } },
          { name: "Déjeuner", foods: [{ n: "Thon frais", q: "170g", p: 42.5, g: 0, l: 1.7, k: 185 }, { n: "Riz basmati", q: "200g", p: 6, g: 50, l: 0.8, k: 230 }, { n: "Asperges", q: "120g", p: 2.6, g: 4.7, l: 0.1, k: 24 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 51.1, g: 54.7, l: 11.8, k: 522 } },
          { name: "Collation", foods: [{ n: "Yaourt grec 0%", q: "200g", p: 20, g: 8, l: 0, k: 112 }, { n: "Beurre de cacahuète", q: "15g", p: 3.8, g: 3, l: 7.5, k: 94 }, { n: "Pain complet", q: "30g", p: 3, g: 13.2, l: 0.8, k: 72 }], total: { p: 26.8, g: 24.2, l: 8.3, k: 278 } },
          { name: "Dîner", foods: [{ n: "Bœuf 5% MG", q: "170g", p: 37.4, g: 0, l: 8.5, k: 226 }, { n: "Pomme de terre", q: "200g", p: 4, g: 36, l: 0.2, k: 162 }, { n: "Haricots verts", q: "150g", p: 2.7, g: 10.5, l: 0.3, k: 47 }], total: { p: 44.1, g: 46.5, l: 9, k: 435 } },
        ],
      },
      {
        total: { prot: 171, glu: 170, lip: 58, kcal: 1835 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Pain complet", q: "100g", p: 10, g: 44, l: 2.5, k: 240 }, { n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Avocat", q: "60g", p: 1.2, g: 5.4, l: 9, k: 96 }, { n: "Fromage blanc 0%", q: "100g", p: 8, g: 4, l: 0.1, k: 49 }], total: { p: 34.4, g: 54.2, l: 23.6, k: 557 } },
          { name: "Déjeuner", foods: [{ n: "Escalope de dinde", q: "200g", p: 48, g: 0, l: 4, k: 228 }, { n: "Boulgour", q: "200g", p: 6, g: 36, l: 0.4, k: 166 }, { n: "Brocoli", q: "150g", p: 4.2, g: 10.5, l: 0.6, k: 51 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 58.2, g: 46.5, l: 14.2, k: 528 } },
          { name: "Collation", foods: [{ n: "Whey Protéine", q: "30g", p: 24, g: 2, l: 1.5, k: 118 }, { n: "Amandes", q: "25g", p: 5.3, g: 5.5, l: 12.5, k: 145 }, { n: "Banane", q: "80g", p: 0.9, g: 18.4, l: 0.2, k: 71 }], total: { p: 30.2, g: 25.9, l: 14.2, k: 334 } },
          { name: "Dîner", foods: [{ n: "Cabillaud", q: "200g", p: 36, g: 0, l: 2, k: 162 }, { n: "Pois chiches", q: "130g", p: 11.7, g: 35.1, l: 3.4, k: 218 }, { n: "Carotte", q: "100g", p: 0.8, g: 8, l: 0.2, k: 36 }], total: { p: 48.5, g: 43.1, l: 5.6, k: 416 } },
        ],
      },
      {
        total: { prot: 148, glu: 176, lip: 70, kcal: 1924 },
        meals: [
          { name: "Petit-déjeuner", foods: [{ n: "Flocons d'avoine", q: "80g", p: 10.4, g: 48, l: 5.6, k: 284 }, { n: "Œuf entier ×2", q: "120g", p: 15.2, g: 0.8, l: 12, k: 172 }, { n: "Miel", q: "15g", p: 0, g: 12.3, l: 0, k: 49 }, { n: "Lait demi-écrémé", q: "200ml", p: 6.4, g: 9.8, l: 3, k: 92 }], total: { p: 32, g: 70.9, l: 20.6, k: 597 } },
          { name: "Déjeuner", foods: [{ n: "Bœuf haché 10%", q: "180g", p: 37.8, g: 0, l: 18, k: 313 }, { n: "Quinoa", q: "180g", p: 7.9, g: 37.8, l: 3.4, k: 216 }, { n: "Tomate", q: "100g", p: 0.9, g: 3.9, l: 0.2, k: 18 }, { n: "Huile d'olive", q: "10ml", p: 0, g: 0, l: 9.2, k: 83 }], total: { p: 46.6, g: 41.7, l: 30.8, k: 630 } },
          { name: "Collation", foods: [{ n: "Cottage cheese", q: "150g", p: 18, g: 4.5, l: 6, k: 144 }, { n: "Beurre de cacahuète", q: "15g", p: 3.8, g: 3, l: 7.5, k: 94 }, { n: "Pomme", q: "120g", p: 0.4, g: 16.8, l: 0.2, k: 62 }], total: { p: 22.2, g: 24.3, l: 13.7, k: 300 } },
          { name: "Dîner", foods: [{ n: "Blanc de poulet", q: "180g", p: 41.4, g: 0, l: 2.7, k: 191 }, { n: "Riz complet", q: "160g", p: 4.2, g: 36.8, l: 1.4, k: 176 }, { n: "Courgette", q: "200g", p: 1.8, g: 2.8, l: 0.8, k: 30 }], total: { p: 47.4, g: 39.6, l: 4.9, k: 397 } },
        ],
      },
    ],
  },
}

const GOAL_LABELS: Record<NutritionPlanId, string> = {
  seche: "Sèche",
  maintien: "Maintien",
  masse: "Prise de masse",
}

function getPlanKeyFromGoal(goalId?: string): NutritionPlanId {
  if (!goalId) return "masse"
  return GOAL_TO_PLAN[goalId] || "masse"
}

// Teinte du conteneur carré de chaque repas (les icônes restent celles de MEAL_ICONS).
const MEAL_TINTS: Record<string, string> = {
  "Petit-déjeuner": "#f59e0b",
  "Déjeuner": "#14b8a6",
  "Collation": "#f43f5e",
  "Dîner": "#8b5cf6",
}

// Clés d'une macro dans NUTRITION_PLANS (key), dans DayData.total (day) et dans MealTotal (meal).
const MACROS = [
  { key: "protein", label: "Protéines", short: "Prot", letter: "P", day: "prot", meal: "p" },
  { key: "carbs", label: "Glucides", short: "Glu", letter: "G", day: "glu", meal: "g" },
  { key: "fat", label: "Lipides", short: "Lip", letter: "L", day: "lip", meal: "l" },
] as const

// ─── JAUGE ──────────────────────────────────────────────────────────────────────

// Arc de 270° ouvert vers le bas, en graduations. Les graduations atteintes passent du violet
// profond au lavande ; une aiguille blanche marque l'apport du jour.
const GAUGE_TICKS = 44
const GAUGE_START_DEG = 135
const GAUGE_SWEEP_DEG = 270
const GAUGE_CENTER = 110
const TICK_FROM = [109, 40, 217] // #6d28d9
const TICK_TO = [196, 181, 253] // #c4b5fd

function tickColor(i: number): string {
  const t = i / (GAUGE_TICKS - 1)
  const [r, g, b] = TICK_FROM.map((from, k) => Math.round(from + (TICK_TO[k] - from) * t))
  return `rgb(${r},${g},${b})`
}

function CalorieGauge({ current, target }: { current: number; target: number }) {
  const pct = Math.min(current / target, 1)
  const litUpTo = Math.round(pct * (GAUGE_TICKS - 1))
  const c = GAUGE_CENTER

  return (
    <svg
      viewBox="0 0 220 186"
      className="w-[220px] max-w-full h-auto"
      role="img"
      aria-label={`${formatIntFr(current)} kcal sur ${formatIntFr(target)} kcal`}
    >
      <defs>
        <filter id="nutrition-gauge-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {Array.from({ length: GAUGE_TICKS }, (_, i) => {
        const a = ((GAUGE_START_DEG + (i / (GAUGE_TICKS - 1)) * GAUGE_SWEEP_DEG) * Math.PI) / 180
        const lit = current > 0 && i <= litUpTo
        return (
          <line
            key={i}
            x1={c + 78 * Math.cos(a)}
            y1={c + 78 * Math.sin(a)}
            x2={c + 91 * Math.cos(a)}
            y2={c + 91 * Math.sin(a)}
            strokeWidth={2.5}
            strokeLinecap="round"
            filter={lit ? "url(#nutrition-gauge-glow)" : undefined}
            style={{ stroke: lit ? tickColor(i) : "rgba(148,163,184,0.16)", transition: `stroke 0.35s ease ${i * 8}ms` }}
          />
        )
      })}

      <g
        style={{
          transform: `rotate(${GAUGE_START_DEG + pct * GAUGE_SWEEP_DEG}deg)`,
          transformOrigin: `${c}px ${c}px`,
          transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <line x1={c + 72} y1={c} x2={c + 97} y2={c} stroke="#fff" strokeWidth={3} strokeLinecap="round" filter="url(#nutrition-gauge-glow)" />
      </g>

      <text x={c} y={c + 6} textAnchor="middle" fill="#fff" fontSize="36" fontWeight="800" style={{ fontVariantNumeric: "tabular-nums" }}>
        {formatIntFr(current)}
      </text>
      <text x={c} y={c + 28} textAnchor="middle" fill="#64748b" fontSize="11" fontFamily="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace">
        / {formatIntFr(target)} kcal
      </text>
    </svg>
  )
}

// ─── COMPONENT ──────────────────────────────────────────────────────────────────

interface WeeklyMealPlanProps {
  goalId?: string
}

// Ouverture depuis la carte « Idée pour … » de l'Apport énergétique : jour affiché et repas déplié.
interface WeekEntryState {
  day?: number
  meal?: string
}

export const WeeklyMealPlan = ({ goalId }: WeeklyMealPlanProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const planKey = getPlanKeyFromGoal(goalId)
  const plan = PLANS[planKey]
  // Cibles de macros = celles de l'Apport énergétique (NUTRITION_PLANS), pas plan.macros.
  const targets = NUTRITION_PLANS[planKey]
  const goalLabel = GOAL_LABELS[planKey]

  const entry = location.state as WeekEntryState | null
  const initialDay =
    entry?.day !== undefined && entry.day >= 0 && entry.day < plan.days.length ? entry.day : 0
  const [selectedDay, setSelectedDay] = useState(initialDay)
  const [expandedMeal, setExpandedMeal] = useState<number | null>(() => {
    const idx = entry?.meal ? plan.days[initialDay].meals.findIndex((m) => m.name === entry.meal) : -1
    return idx >= 0 ? idx : null
  })
  const [animating, setAnimating] = useState(false)
  const openMealRef = useRef<HTMLButtonElement>(null)

  // Repas déplié à l'ouverture (venant de l'idée de repas) : le ramener dans le champ.
  useEffect(() => {
    openMealRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [])

  const dayData = plan.days[selectedDay]

  const handleDayChange = (idx: number) => {
    if (idx === selectedDay) return
    setAnimating(true)
    setTimeout(() => {
      setSelectedDay(idx)
      setExpandedMeal(null)
      setAnimating(false)
    }, 150)
  }

  const diff = dayData.total.kcal - plan.targetKcal

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#060a13] pb-32 cursor-default relative"
    >
      <NutritionBackdrop />

      <div className="relative z-10">
        <div className="px-5 pt-5">
          <NutritionHeader title="Semaine type" onBack={() => navigate("/dashboard/nutrition")}>
            <PlanPill label={goalLabel} />
            <p className="text-[14px] font-bold text-white tabular-nums">
              {formatIntFr(plan.targetKcal)} <span className="font-mono text-[11px] font-medium text-gray-400">kcal / jour</span>
            </p>
          </NutritionHeader>
        </div>

        {/* SÉLECTEUR DE JOURS */}
        <div className="grid grid-cols-7 gap-1.5 px-4 mt-5">
          {DAYS.map((d, i) => {
            const active = selectedDay === i
            return (
              <button
                key={d}
                type="button"
                aria-label={d}
                aria-pressed={active}
                onClick={() => handleDayChange(i)}
                className={cn(
                  "flex flex-col items-center gap-0.5 rounded-xl border py-2.5 cursor-pointer transition-all",
                  active
                    ? "border-violet-400/60 text-white"
                    : "border-white/[0.06] bg-[#0c1120]/85 text-gray-300 hover:border-white/[0.12]"
                )}
                style={active ? { background: "linear-gradient(160deg, #8b5cf6, #6d28d9)", boxShadow: "0 6px 18px rgba(139,92,246,0.4)" } : undefined}
              >
                <span className={cn("font-mono text-[9px] font-bold uppercase tracking-[0.12em]", active ? "text-violet-100" : "text-gray-500")}>
                  {DAY_SHORT[i]}
                </span>
                <span className="text-[17px] font-extrabold leading-tight">{i + 1}</span>
              </button>
            )
          })}
        </div>

        <div
          className={`px-4 mt-4 transition-all duration-150 ${
            animating ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
          }`}
        >
          {/* BILAN DU JOUR */}
          <div className="relative overflow-hidden rounded-3xl border p-4 pt-5" style={haloSurface("100% 0%")}>
            <div className="flex flex-col items-center">
              <CalorieGauge current={dayData.total.kcal} target={plan.targetKcal} />
              <span
                className={cn(
                  "-mt-1 inline-flex rounded-full border px-3 py-1 font-mono text-[11px] font-bold tabular-nums",
                  diff < 0
                    ? "border-orange-400/35 bg-orange-500/10 text-orange-300"
                    : "border-emerald-400/35 bg-emerald-500/10 text-emerald-300"
                )}
              >
                {diff >= 0 ? "+" : ""}{formatIntFr(diff)} kcal vs objectif
              </span>
            </div>

            <div className="mt-5 space-y-3">
              {MACROS.map((m) => {
                const value = dayData.total[m.day]
                const target = targets.macros[m.key]
                const color = MACRO_COLORS[m.key]
                return (
                  <div key={m.key} className="flex items-center gap-2.5">
                    <span className="w-[64px] shrink-0 text-[12px] text-gray-400">{m.label}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-[width] duration-500"
                        style={{ width: `${Math.min((value / target) * 100, 100)}%`, background: color, boxShadow: `0 0 8px ${color}66` }}
                      />
                    </div>
                    <span className="w-[84px] shrink-0 text-right font-mono text-[11.5px] tabular-nums">
                      <span className="font-bold" style={{ color }}>{value}g</span>
                      <span className="text-gray-500"> / {target}g</span>
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* REPAS DU JOUR */}
          <div className="mt-6">
            <SectionLabel>Repas du jour</SectionLabel>
          </div>

          {dayData.meals.map((meal, mi) => {
            const isOpen = expandedMeal === mi
            return (
              <button
                key={mi}
                type="button"
                ref={isOpen ? openMealRef : undefined}
                aria-expanded={isOpen}
                onClick={() => setExpandedMeal(isOpen ? null : mi)}
                className={cn(
                  CARD,
                  "block w-full text-left mb-2.5 overflow-hidden cursor-pointer transition-colors",
                  isOpen ? "border-violet-400/30" : "hover:border-white/[0.12]"
                )}
              >
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <IconTile color={MEAL_TINTS[meal.name] ?? "#8b5cf6"} className="w-11 h-11 text-[20px] leading-none">
                    {MEAL_ICONS[meal.name] || "🍴"}
                  </IconTile>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-bold text-white">{meal.name}</p>
                    <p className="font-mono text-[12px] text-gray-500 mt-0.5">{formatIntFr(meal.total.k)} kcal</p>
                  </div>
                  <div className="flex flex-col items-end font-mono text-[11px] font-bold leading-[1.35] tabular-nums">
                    {MACROS.map((m) => (
                      <span key={m.key} style={{ color: MACRO_COLORS[m.key] }}>
                        {Math.round(meal.total[m.meal])}{m.letter}
                      </span>
                    ))}
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                  />
                </div>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? "max-h-[400px]" : "max-h-0"
                  }`}
                >
                  <div className="mx-4 pb-3.5 border-t border-white/[0.06]">
                    {meal.foods.map((f, fi) => (
                      <div
                        key={fi}
                        className={cn(
                          "flex items-center justify-between gap-3 py-2.5",
                          fi < meal.foods.length - 1 && "border-b border-white/[0.04]"
                        )}
                      >
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-white">{f.n}</p>
                          <p className="font-mono text-[10.5px] text-gray-500">{f.q}</p>
                        </div>
                        <span className="shrink-0 font-mono text-[11px] text-gray-400">
                          {formatIntFr(f.k)} <span className="text-[9px] text-gray-500">kcal</span>
                        </span>
                      </div>
                    ))}

                    <div className="grid grid-cols-3 gap-1.5 mt-2.5">
                      {MACROS.map((m) => (
                        <div
                          key={m.key}
                          className="rounded-lg py-1.5 text-center"
                          style={{ background: `${MACRO_COLORS[m.key]}14` }}
                        >
                          <p className="text-[13px] font-bold tabular-nums" style={{ color: MACRO_COLORS[m.key] }}>
                            {Math.round(meal.total[m.meal])}g
                          </p>
                          <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-gray-500 mt-0.5">{m.short}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}

          <div className="mt-4">
            <InfoBanner>
              Ce plan est un exemple pour vous inspirer. Adaptez les aliments et quantités selon vos préférences tout en respectant vos objectifs de macros.
            </InfoBanner>
          </div>
        </div>
      </div>

      <BottomNav />
    </motion.div>
  )
}
